import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, TrendingUp, CheckCircle2, FlaskConical } from 'lucide-react';
const services = [{
  id: 'strategy',
  title: 'Strategy',
  icon: Target,
  tagline: 'Define bold growth bets and decide what matters most, now',
  description: 'When you have 10+ competing growth ideas, a budget cycle coming up and targets you must meet, we help you land on 2-3 credible bets your finance, product and sales leaders can commit to.',
  deliverables: ['Growth hypothesis development', 'Taking innovation ideas to strategy and plan', 'Portfolio and bet selection', 'Business cases for investment', 'Simple, sharp exec narratives'],
  caseStudy: {
    company: 'ERP AI-Led Growth Strategy',
    result: 'Drove growth strategy for global ERP vendor. Developed opportunity portfolio, drove bet selection, created executive walking deck and team execution roadmap mapped to GTM framework.'
  }
}, {
  id: 'incubation',
  title: 'Incubation',
  icon: FlaskConical,
  tagline: 'Validate in the real world to get signal, fast',
  description: 'When leadership is intrigued but unconvinced, our multi-disciplinary team stands up small, contained experiments that answer "is this worth scaling?" while minimizing impact on your ongoing operations.',
  deliverables: ['Pilot design (MVP, POC) and execution', 'Tests for customer, pricing, product insight', 'Refined biz models, cases for exit decisions', 'Incubation plans and pitch for resources', 'Incubation to scale transition plans'],
  caseStudy: {
    company: 'New Channel for $1B+ Tech Retailer',
    result: 'Developed third-party marketplace strategy, ran pilot and built middleware before scaling to internal operations. Realized 150% gain in margin recovery on EOL product and scaled to > 500K units sold to date.'
  }
}, {
  id: 'scale',
  title: 'Scale',
  icon: TrendingUp,
  tagline: 'Scale what works, turn wins into rhythm',
  description: 'When an early win pattern emerges in one region, product, or partner, we help turn it into a repeatable play with GTM programs, marketing and sales motions, enablement and operating rhythm.',
  deliverables: ['GTM and channel plans and programs', 'Field/partner sales plays and enablement', 'Team structure and capability building', 'Operational playbooks, ROB and cadence', 'Performance tracking, metrics, optimization'],
  caseStudy: {
    company: 'Emerging Markets Channel Incentive Program',
    result: 'We designed a POS incentive program and developed a mobile technology platform to support it. Successfully piloted in five markets, we expanded to 22 countries. Result: 92% increase in premium SKU sales.'
  }
}];
export function ServiceLines() {
  const [activeTab, setActiveTab] = useState('strategy');
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 100
  });
  const buttonRefs = useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});
  const activeService = services.find(s => s.id === activeTab) || services[0];
  // Calculate indicator position based on active button
  useEffect(() => {
    const activeButton = buttonRefs.current[activeTab];
    if (activeButton) {
      const buttonRect = activeButton.getBoundingClientRect();
      const containerRect = activeButton.parentElement?.getBoundingClientRect();
      if (containerRect) {
        const left = buttonRect.left - containerRect.left;
        const center = left + buttonRect.width / 2;
        setIndicatorStyle({
          left: center - 50,
          width: 100
        });
      }
    }
  }, [activeTab]);
  return <section id="services" className="relative py-32 px-6 lg:px-8 bg-[#0A1628] overflow-hidden" data-id="element-170">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-[#0F52BA]/20 to-transparent rounded-full blur-3xl" data-id="element-171"></div>
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-[#06B6D4]/10 to-transparent rounded-full blur-3xl" data-id="element-172"></div>

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-173">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-174">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-white" style={{
          fontFamily: 'Space Grotesk, sans-serif'
        }} data-id="element-175">
            Hypothesis to scaled impact
          </h2>
          <p className="text-xl leading-relaxed text-gray-300" data-id="element-176">
            Explore our three integrated service lines
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="relative mb-12" data-id="element-177">
          <div className="flex justify-center gap-2 md:gap-4 mb-2" data-id="element-178">
            {services.map(service => {
            const Icon = service.icon;
            const isActive = activeTab === service.id;
            return <button key={service.id} ref={el => buttonRefs.current[service.id] = el} onClick={() => setActiveTab(service.id)} className={`relative px-6 md:px-10 py-4 rounded-full font-semibold transition-all duration-300 ${isActive ? 'text-white' : 'text-gray-400 hover:text-gray-200'}`} data-id="element-179">
                  <div className="flex items-center gap-2 md:gap-3" data-id="element-180">
                    <Icon className="w-5 h-5" data-id="element-181" />
                    <span className="text-sm md:text-base" data-id="element-182">
                      {service.title}
                    </span>
                  </div>

                  {isActive && <motion.div layoutId="activeTab" className="absolute inset-0 bg-white/5 border border-white/20 rounded-full" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-183" />}
                </button>;
          })}
          </div>

          {/* Sliding indicator */}
          <div className="relative h-1" data-id="element-184">
            <motion.div className="absolute h-1 bg-gradient-to-r from-[#0F52BA] to-[#06B6D4] rounded-full" animate={{
            left: indicatorStyle.left,
            width: indicatorStyle.width
          }} transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.6
          }} data-id="element-185" />
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait" data-id="element-186">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 max-w-5xl mx-auto" data-id="element-187">
            {/* Header */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="mb-8" data-id="element-188">
              <div className="inline-flex items-center gap-3 mb-4" data-id="element-189">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0F52BA] to-[#06B6D4] flex items-center justify-center" data-id="element-190">
                  {createElement(activeService.icon, {
                  className: 'w-6 h-6 text-white'
                })}
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white" style={{
                fontFamily: 'Space Grotesk, sans-serif'
              }} data-id="element-191">
                  {activeService.title}
                </h3>
              </div>
              <p className="text-xl text-gray-300 font-medium" data-id="element-192">
                {activeService.tagline}
              </p>
            </motion.div>

            {/* Description */}
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg leading-relaxed text-gray-300 mb-10 whitespace-pre-line" data-id="element-193">
              {activeService.description}
            </motion.p>

            {/* Two column layout */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-194">
              {/* Key Deliverables */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} data-id="element-195">
                <h4 className="text-lg font-bold text-white mb-4" data-id="element-196">
                  Key Deliverables
                </h4>
                <ul className="space-y-3" data-id="element-197">
                  {activeService.deliverables.map((item, index) => <motion.li key={index} initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.4 + index * 0.1
                }} className="flex items-start gap-3" data-id="element-198">
                      <CheckCircle2 className="w-5 h-5 text-[#06B6D4] flex-shrink-0 mt-0.5" data-id="element-199" />
                      <span className="text-gray-300" data-id="element-200">{item}</span>
                    </motion.li>)}
                </ul>
              </motion.div>

              {/* Case Study */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} className="bg-white/5 border border-white/10 rounded-2xl p-6" data-id="element-201">
                <div className="text-xs font-semibold text-[#06B6D4] uppercase tracking-wider mb-3" data-id="element-202">
                  Case Study
                </div>
                <h4 className="text-lg font-bold text-white mb-3" data-id="element-203">
                  {activeService.caseStudy.company}
                </h4>
                <p className="text-gray-300 leading-relaxed" data-id="element-204">
                  {activeService.caseStudy.result}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="mt-16 text-center" data-id="element-205">
          <p className="text-lg mb-6 leading-relaxed text-gray-300" data-id="element-206">
            Ready to turn your growth challenge into decisive action?
          </p>
          <a href="#contact" className="inline-block px-8 py-4 bg-gradient-to-r from-[#0F52BA] to-[#06B6D4] text-white text-base font-semibold rounded-full hover:shadow-xl hover:shadow-[#0F52BA]/30 hover:scale-105 transition-all duration-200" data-id="element-207">
            Let's Talk
          </a>
        </motion.div>
      </div>
    </section>;
}